<template>
    <popup-modal
        :close-on-click="!form.isDirty()"
        customClasses="w-960 max-w-full"
        :title="$t('mailer.header')"
        @close="$emit('close')"
    >

        <div>
            <banner
                danger
                :description="$tc('mail.warnings.not-allowed', applications.length)"
                inline
                v-if="amountValidEmails === 0"
            />
            <div v-else-if="amountInvalidEmails > 0"
                 class="font-semibold text-semantic-warning mb-16 p-16 bg-semantic-warning-light border border-semantic-warning rounded">
                {{ $t('mail.warnings.missing-emails') }}
                <ul class="pt-4 flex flex-wrap text-primary font-normal">
                    <li
                        v-for="application in applications.filter(i => !i.email)"
                        :key="application.id"
                        class="inline-flex items-center mr-8 my-4"
                    >
                        <avatar
                            :color="application.stageColor"
                            :image="application.image_url"
                            size="small"
                            class="mr-4"
                        >
                            {{ application.initials }}
                        </avatar>
                        <span>{{ application.fullName }}</span>
                    </li>
                </ul>
            </div>

            <div class="mb-16">
                <Recipients
                    :is-bulk-based-on-count="isBulkBasedOnCount"
                    :applications-count="applicationsCount"
                    :total-applications="totalApplications"
                    :applications="applications"
                    :fetching-data="fetchingData"
                    :can-edit="context !== 'single'"
                    @updateApplicants="updateApplications"
                />
            </div>

            <label class="mb-8 flex items-center font-semibold text-black" for="bcc-search-input">
                {{ $t('mailer.add_bcc') }}
                <span class="ml-4">{{ $t('general.optional') }}</span>
            </label>
            <bcc-fields v-model="form.bcc" class="mb-16"></bcc-fields>

            <select-dropdown
                static-id="emailTemplateNameDropdown"
                v-model="selectedTemplate"
                :auto-translate="false"
                :options="templateChoices"
                no-auto-sort
                class="mb-16"
                searchable
                @change="hydrateFromTemplate"
            >
                {{ $t('mailer.choose_template') }}

                <tabs v-if="languages"
                      :active-key="(templateLocale ? templateLocale : active_locale)"
                      :items="languages"
                      class="inline-flex w-full mb-8"
                      scroll
                      @set-active="hydrateFromTemplate"
                >
                </tabs>
            </select-dropdown>

            <simple-input
                class="mb-16"
                :input-label="$t('mailer.subject')"
                :required="true"
                v-model="form.subject"
            />
            <div v-if="errors.subject !== undefined && errors.subject.length" class="flex items-center text-semantic-error text-body-sm mb-16">
                <svg class="block fill-current flex-none trans-ease max-w-12 max-h-12 mr-4">
                    <use xlink:href="#warning"></use>
                </svg>
                <span>{{ errors.subject }}</span>
            </div>

            <!--Description-->
            <wysiwyg
                ref="content"
                v-model="form.message"
                :placeholders="['application', 'company', 'user']"
                class="mb-16"
                with-links
            >
                {{ $t('mailer.description') }}
                <template #after>
                    <div
                        :class="[ signaturePreview ? 'items-start' : 'items-center text-primary-40' ]"
                        class="flex p-16 border-t border-primary-20"
                    >
                        <div class="text-body flex-grow">
                            <span v-if="signaturePreview" v-html="signaturePreview"></span>
                            <span v-else>{{ $t('email_signatures.no_signature_selected') }}</span>
                        </div>
                        <select-dropdown
                            v-model="selectedSignature"
                            :auto-translate="false"
                            :options="signatureChoices"
                            class="min-w-240"
                        ></select-dropdown>
                    </div>
                </template>
            </wysiwyg>

            <file-upload
                ref="fileUpload"
                v-model="form.attachments"
                multiple
                @removed="removeFile"
            >
                <!--show exsisting files-->
                <ul
                    class="rounded-sm overflow-hidden w-full mb-16">
                    <li
                        v-for="attachment in form.attachments"
                        v-show="attachment.locale === active_locale"
                        :key="'attachment-' + attachment.id"
                        class="odd:bg-primary-5 relative"
                    >
                        <a
                            :download="!extensions.includes(attachment.extension) ? attachment.original_name : null"
                            :href="attachment.url"
                            :target="extensions.includes(attachment.extension) ? '_blank' : null"
                            class="group flex items-center p-16 cursor-pointer">
                            <div class="mr-auto font-semibold group-hover:text-primary trans-ease">{{ attachment.original_name }}</div>
                            <div class="pl-16">
                                <div class="h-32 w-32 flex items-center justify-center rounded-sm text-primary-80 trans-ease group-hover:text-primary mr-32">
                                    <svg-icon class="max-w-16 max-h-16" type="download"></svg-icon>
                                </div>
                            </div>
                        </a>

                        <div class="absolute right-0 inset-y-0 mr-16 flex items-center pointer-events-none">
                            <button class="text-primary-80 hover:text-semantic-error cursor-pointer trans-ease pointer-events-auto h-32 w-32 flex items-center justify-center rounded-sm"
                                    @click="confirmDeleteAttachment(attachment)">
                                <svg-icon class="max-w-16 max-h-16" type="delete"></svg-icon>
                            </button>
                        </div>
                    </li>
                </ul>
            </file-upload>

            <banner
                v-if="!currentCompany.is_demo && currentCompany.in_trial"
                :description="$t('applications.no_mails_in_free_trial')"
                inline
                warning
            />

            <!--Submit button-->
            <div class="flex justify-end items-center pt-32">
                <simple-button
                    v-if="contact"
                    :loading="sendingMail"
                    class="mr-auto"
                    display="danger"
                    icon="delete"
                    @click="destroy"
                >
                    {{ $t('general.delete') }}
                </simple-button>
                <simple-dropdown on-top position="right">
                    <template #trigger>
                        <simple-button
                            :disabled="amountValidEmails === 0"
                            :loading="sendingMail"
                            class="mr-8"
                            look="primary"
                            icon="schedule"
                        >
                            <template v-if="!form.scheduled_at">
                                {{ $t('mailer.plan_email') }}
                            </template>
                            <template v-else>
                                {{ $t('general.scheduled_for_date', {date: moment(form.scheduled_at).local().format('DD/MM/YYYY HH:mm')}) }}
                            </template>
                        </simple-button>
                    </template>
                    <template #content>
                        <div class="bg-white rounded-sm w-360 overflow-hidden -space-y-px">
                            <div v-for="(option, i) in scheduleOptions" :key="option.key" :class="[option.key === scheduleOption ? 'bg-secondary-5 border-secondary z-10' : 'border-primary-20', i === 0 ? 'rounded-t' : '', i === scheduleOptions.length - 1 ? 'rounded-b' : '']"
                                 class="relative border p-16 flex cursor-pointer"
                                 @click="option.fn">
                                <div class="flex items-center h-12">
                                    <input :checked="option.key === scheduleOption" class="form-radio h-16 w-16 text-secondary trans-ease rounded-32 border-primary-20" type="radio">
                                </div>
                                <div class="ml-8 flex flex-col cursor-pointer">
                                    <span :class="option.key === scheduleOption ? 'text-secondary' : 'text-primary-80'" class="block leading-5">
                                        {{ option.title }}
                                    </span>
                                    <span v-if="option.key !== 'custom'" :class="option.key === scheduleOption ? 'text-secondary-80' : 'text-primary-80'"
                                          class="block text-body-sm leading-5 mt-2">
                                        {{ option.description }}
                                    </span>
                                    <div v-else class="mt-8 w-240">
                                        <date-picker
                                            class="w-full"
                                            :disabled="scheduleOption !== 'custom'"
                                            disable-past
                                            size-class="h-32 px-8"
                                            time
                                            :value="scheduleOption === 'custom' ? form.scheduled_at : null"
                                            white-background
                                            @input="$event ? form.scheduled_at = $event : undefined"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </simple-dropdown>
                <simple-button
                    :disabled="amountValidEmails === 0"
                    :loading="sendingMail"
                    display="secondary"
                    look="primary"
                    icon="send"
                    @click="validateBeforeSubmit"
                >
                    <template v-if="contact">
                        {{ form.scheduled_at ? $t('general.save') : $t('general.send') }}
                    </template>
                    <template v-else>
                        {{ form.scheduled_at ? $t('mailer.plan') : $t('general.send') }}
                    </template>
                </simple-button>
            </div>
        </div>

        <popup-modal-confirm
            v-if="!!bulkApplicationData"
            :confirmText="$t('general.confirm')"
            :message="$tc('mailer.confirmation', bulkCount, {count: bulkCount})"
            :working="performingContactRequest"
            confirmButton="success"
            @close="cancelVerification"
            @confirm="send"
        />

        <popup-modal-confirm
            v-if="confirmDeletion"
            :confirmText="$t('general.confirm')"
            :message="$t('general.confirm_delete_subject', { subject: 'email' })"
            :working="performingContactRequest"
            @close="stopDelete"
            @confirm="destroy(true)"
        />

        <popup-modal-confirm
            v-if="deletingAttachment"
            :confirmText="$t('general.delete_subject', {subject: $t('general.attachment')})"
            :message="$t('general.confirm_delete_subject', {subject: deletingAttachment.original_name})"
            :working="performingDeleteAttachmentRequest"
            @close="cancelDeleteAttachment"
            @confirm="deleteAttachment(deletingAttachment)"
        >
        </popup-modal-confirm>

    </popup-modal>
</template>

<script>
import PopupModal from './PopupModal.vue';
import Wysiwyg from '../forms/Wysiwyg.vue';
import Input from '../forms/Input.vue';
import SimpleButton from './SimpleButton.vue';
import * as templates from '../../services/EmailTemplateService';
import * as documents from '../../services/DocumentService';
import EmailTemplate from '../../models/EmailTemplate';
import {normalizeJson, openableFileExtensionsInWebBrowser} from '../../../shared/helpers';
import {useRepo} from 'pinia-orm';
import SelectDropdown from '../forms/SelectDropdown.vue';
import Avatar from './Avatar.vue';
import FileUpload from '../forms/FileUpload';
import PopupModalConfirm from './PopupModalConfirm.vue';
import sendsMails from '../../mixins/sendsMails';
import SimpleDropdown from './SimpleDropdown.vue';
import Checkbox from '../forms/Checkbox.vue';
import DatePicker from '../forms/DatePicker.vue';
import moment from 'moment';
import Document from '../../models/Document';
import SvgIcon from './SvgIcon.vue';
import Tabs from '../navigation/Tabs.vue';
import Loading from './Loading.vue';
import * as bulkApplicationKey from '../../services/BulkApplicationKeyService';
import BccFields from './BccFields.vue';
import Recipients from "../mails/forms/Recipients.vue";
import Banner from "./Banner.vue";

export default {
    components: {
        Banner,
        Recipients,
        BccFields,
        Loading,
        Tabs,
        DatePicker,
        Checkbox,
        SimpleDropdown,
        'popup-modal': PopupModal,
        'simple-input': Input,
        'wysiwyg': Wysiwyg,
        'simple-button': SimpleButton,
        'select-dropdown': SelectDropdown,
        'avatar': Avatar,
        'file-upload': FileUpload,
        'popup-modal-confirm': PopupModalConfirm,
        'svg-icon': SvgIcon,
    },
    mixins: [sendsMails],
    data: function () {
        return {
            errors: [],
            selectedTemplate: null,
            template: null,
            templateLocale: '',
            performingContactRequest: false,
            confirmDeletion: false,
            extensions: openableFileExtensionsInWebBrowser,
            deletingAttachment: null,
            performingDeleteAttachmentRequest: false,
            fetchingData: false,
            bulkKeyDataForCount: null,
            templateType: 'general',
        };
    },
    methods: {
        validateBeforeSubmit() {
            this.errors = [];

            //Validate form
            if (!this.form.data.subject.length) {
                this.errors = {...this.errors, subject: this.$t('validation.contact_subject_required')};

                return false;
            }

            //Get the application data
            this.contact ? this.update() : this.getBulkApplicationKey();
        },
        moment,
        send() {
            if (this.performingContactRequest) return;
            this.performingContactRequest = true;

            this.performSend()
                .then(inserts => {
                    this.performingContactRequest = false;
                    this.bulkApplicationData = null;
                    this.$emit('close');
                })
                .catch(() => {
                    this.performingContactRequest = false;
                    this.cancelVerification();
                });
        },
        update() {
            this.performingContactRequest = true;

            this.performUpdate()
                .then(() => {
                    this.performingContactRequest = false;
                    this.$emit('close', true);
                })
                .finally(() => {
                    this.performingContactRequest = false;
                });
        },
        destroy(confirm = false) {
            if (!confirm) {
                return this.confirmDeletion = true;
            }

            this.performingContactRequest = true;

            this.performDelete()
                .then(() => {
                    this.performingContactRequest = false;
                    this.$emit('close', true);
                })
                .catch(() => {
                    this.performingContactRequest = false;
                });
        },
        stopDelete() {
            this.conirmDeletion = false;
        },
        hydrateFromTemplate(data) {
            let lang = isNaN(data) ? data : this.active_locale;
            let templateId = !isNaN(data) ? data : this.selectedTemplate;

            this.templateLocale = lang;

            let template = this.template = this.templates.find(template => template.id === templateId);

            //Always empty attachments
            this.form.attachments = [];

            if (typeof template != 'undefined') {
                //Check for attachments
                if (template.attachments.length > 0) {
                    //Add attachments to form by locale
                    this.form.attachments = template.attachments.filter(obj => obj.locale === lang);
                }

                this.form.subject = template.subject[lang];
                this.$refs.content.setContent(template.content[lang]);
            }
        },
        removeFile({rawFile}) {
            if (!rawFile.id) {
                return;
            }

            documents.destroy(rawFile.id)
                .then(() => Document.delete(rawFile.id));
        },

        //Show popup to remove attachments added by default
        confirmDeleteAttachment(attachment) {
            this.deletingAttachment = attachment;
        },
        cancelDeleteAttachment() {
            this.deletingAttachment = null;
        },
        deleteAttachment(attachment) {
            this.performingDeleteAttachmentRequest = true;

            //Remove from list, not from default template
            this.form.attachments = this.form.attachments.filter(att => att.id !== attachment.id);

            this.deletingAttachment = null;
            this.performingDeleteAttachmentRequest = false;
        },
        getCount() {
            this.form.submit(data => bulkApplicationKey.get({
                    data: {
                        selection: {
                            applications: this.applications,
                            stages: this.stages,
                        },
                        relationships: {
                            stage: {
                                data: {
                                    type: 'stages',
                                    id: data.stage,
                                },
                            },
                        },
                    },
                }, this.requestFilters),
            ).then(data => this.bulkKeyDataForCount = data)
                .finally(() => {
                    this.fetchingData = false;
                });
        },
    },
    mounted() {
        this.templateLocale = this.active_locale;
        templates.index('general').then(result => {
            return useRepo(EmailTemplate).save(normalizeJson(result.data));
        });

        Promise.all(this.documents.map(i => i.toFile()))
            .then(files => {
                this.$refs.fileUpload.setFiles(files);
            });

        if (!this.applications.length) {
            this.fetchingData = true;
            this.getCount();
        }
    },
};
</script>


<style scoped>
[type='radio']:checked {
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='8' cy='8' r='3'/%3e%3c/svg%3e");
}

[type='radio']:checked {
    border-color: transparent;
    background-color: currentColor;
    background-size: 100% 100%;
    background-position: center;
    background-repeat: no-repeat;
}

[type='checkbox'], [type='radio'] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    padding: 0;
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
    display: inline-block;
    vertical-align: middle;
    background-origin: border-box;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    flex-shrink: 0;
    background-color: #fff;
    border-width: 1px;
}
</style>
